import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Types for order operations
export interface OrderData {
  fromAddress: any;
  toAddress: any;
  parcels: {
    id: string;
    quantity: number;
    weight: number;
    length: number;
    width: number;
    height: number;
    physicalWeight: number;
    dimensionalWeight: number;
    price?: number;
    showPrice?: boolean;
    items?: Array<{
      id: string;
      description: string;
      quantity: number;
      weight: number;
      price: number;
      madeIn: string;
    }>;
  }[];
  documents: any[];
  selectedRate: any;
  shippingType: 'package' | 'document';
  orderDetails: {
    totalWeight: number;
    totalItems: number;
    estimatedDelivery: string;
    service: string;
  };
  pricing: {
    shipping: number;
    insurance: number;
    tax: number;
    total: number;
  };
  paymentMethod?: string;
  paymentMethodId?: string;
  referenceNumber?: string;
  default_shipping_id?: string;
  default_shipping_name?: string;
  save_as_draft?: boolean;
}

export interface Address {
  id: number;
  city: {
    id: number;
    name: string;
    latitude: string;
    longitude: string;
  };
  state: {
    id: number;
    name: string;
    type: string;
    latitude: string;
    longitude: string;
    state_code: string;
  };
  country: {
    id: number;
    name: string;
    iso2: string;
    iso3: string;
    emoji: string;
    currency: string;
    currency_name: string;
    currency_symbol: string;
    // ... other fields
  };
  email: string;
  phone: string;
  street: string;
  userId: number;
  houseNo: string;
  zipCode: string;
  nickname: string;
  companyName: string | null;
  contactName: string;
  deliveryDetails: string | null;
  isDefault: number;
  isFavorite: number;
  // ... other fields
}

export interface Parcel {
  id: string;
  width: number;
  height: number;
  length: number;
  weight: number;
  currency: string;
  quantity: number;
  showPrice: boolean;
  physicalWeight: number;
  dimensionalWeight: number;
  dimensionsDisplay: string;
  price: number;
  items?: Array<{
    id: string;
    description: string;
    quantity: number;
    weight: number;
    price: number;
    madeIn: string;
  }>;
  // ... other fields
}

export interface Rate {
  id: string;
  name: string;
  price: number;
  currency: string;
  provider: string;
  deliveryTime: string;
}

export interface OrderResponse {
  id: number;
  order_number: string;
  user_id: number;
  status: string; // Legacy status field
  status_label: string; // Legacy status label
  
  // New status fields
  order_status?: 'draft' | 'created' | 'processing' | 'completed' | 'cancelled' | 'failed';
  payment_status?: 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded';
  shipment_status?: 'pending' | 'ready' | 'scheduled_for_pickup' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception' | 'cancelled';
  
  from_address: Address;
  to_address: Address;
  selected_rate: Rate;
  parcels: Parcel[];
  documents: any[];
  reference_number: string;
  currency: string;
  delivery_estimate: string;
  is_document: boolean;
  billing_name: string;
  payment_method: string;
  payment_method_id: string;
  payment_transaction_id: string | null;
  payment_details: any | null;
  total_price: number;
  carrier: string;
  service: string;
  tracking_number: string;
  shipment_id: string;
  provider: string;
  label_format: string;
  label_content: string | null;
  label_url: string | null;
  paid_at: string;
  shipped_at: string;
  delivered_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;

  // Legacy fields for backward compatibility
  orderNumber?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  createdAt?: string;
  fromAddress?: Address;
  toAddress?: Address;
  selectedRate?: Rate;
  referenceNumber?: string;
  deliveryEstimate?: string;
  isDocument?: boolean;
  billingName?: string;
  paymentMethod?: string;
  paymentMethodId?: string;
  totalPrice?: number;
  labelFormat?: string;
  labelContent?: string;
  labelUrl?: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  updatedAt?: string;
  paidAt?: string;
}

// Interface for pagination metadata
export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
}

// Interface for API response with pagination
export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

const ordersApi = {
  /**
   * Create a new order
   */
  createOrder: async (orderData: OrderData): Promise<OrderResponse> => {
    try {
      const response = await api.post('/orders', orderData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  /**
   * Update an existing order
   */
  updateOrder: async (
    orderId: string | number,
    orderData: OrderData
  ): Promise<OrderResponse> => {
    try {
      const response = await api.put(`/orders/${orderId}`, orderData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get order by ID
   */
  getOrder: async (orderId: string | number): Promise<OrderResponse> => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get all orders for current user with pagination
   */
  getOrders: async (page = 1, limit = 10, filters = {}) => {
    try {
      const response = await api.get('/orders', {
        params: { 
          page, 
          limit,
          ...filters
        }
      });
      
      // Handle case where API doesn't return paginated response
      if (!response.data.meta) {
        return {
          data: response.data.data || [],
          meta: {
            current_page: 1,
            from: 1,
            last_page: 1,
            per_page: response.data.data?.length || 0,
            to: response.data.data?.length || 0,
            total: response.data.data?.length || 0
          }
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  /**
   * Cancel an order
   */
  cancelOrder: async (orderId: string | number): Promise<any> => {
    try {
      const response = await api.post(`/orders/${orderId}/cancel`);
      return response.data.data;
    } catch (error) {
      console.error(`Error canceling order ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Download shipping label
   */
  downloadLabel: async (orderId: string | number): Promise<any> => {
    try {
      const response = await api.get(`/orders/${orderId}/label`);
      return response.data;
    } catch (error) {
      console.error(`Error downloading label for order ${orderId}:`, error);
      throw error;
    }
  }
};

// Main hook for order operations
export const useOrders = () => {
  const queryClient = useQueryClient();

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (data: OrderData) => ordersApi.createOrder(data),
    onSuccess: (data) => {
      // Invalidate orders cache to force refetch
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      // Store the new order in cache
      queryClient.setQueryData(['order', data.id], data);
    },
  });

  // Update order mutation (e.g., save as draft)
  const updateOrderMutation = useMutation({
    mutationFn: (variables: { orderId: string | number; data: OrderData }) =>
      ordersApi.updateOrder(variables.orderId, variables.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.setQueryData(['order', data.id], data);
    },
  });

  // Cancel order mutation
  const cancelOrderMutation = useMutation({
    mutationFn: (orderId: string | number) => ordersApi.cancelOrder(orderId),
    onSuccess: (data, orderId) => {
      // Invalidate both orders list and the specific order
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    },
  });

  // Download label mutation
  const downloadLabelMutation = useMutation({
    mutationFn: (orderId: string | number) => ordersApi.downloadLabel(orderId),
  });

  // Get all orders query with pagination
  const useOrdersQuery = (page = 1, limit = 10, filters = {}, enabled = true) => {
    return useQuery({
      queryKey: ['orders', page, limit, filters],
      queryFn: () => ordersApi.getOrders(page, limit, filters),
      enabled: enabled, // Only fetch when enabled (authenticated)
      staleTime: 60000, // 1 minute
      refetchOnWindowFocus: false
    });
  };

  return {
    // Create order
    createOrder: (data: OrderData) => createOrderMutation.mutateAsync(data),
    isCreatingOrder: createOrderMutation.isPending,
    createOrderError: createOrderMutation.error,
    newOrder: createOrderMutation.data,

    // Get orders with pagination
    useOrdersQuery,

    // Cancel order
    cancelOrder: (orderId: string | number) => cancelOrderMutation.mutateAsync(orderId),
    isCancelingOrder: cancelOrderMutation.isPending,
    cancelOrderError: cancelOrderMutation.error,

    // Update order (save as draft)
    updateOrder: (orderId: string | number, data: OrderData) =>
      updateOrderMutation.mutateAsync({ orderId, data }),
    isUpdatingOrder: updateOrderMutation.isPending,
    updateOrderError: updateOrderMutation.error,

    // Download label
    downloadLabel: (orderId: string | number) => downloadLabelMutation.mutateAsync(orderId),
    isDownloadingLabel: downloadLabelMutation.isPending,
    downloadLabelError: downloadLabelMutation.error,

    // Get single order function
    getOrderById: (orderId: string | number) => ordersApi.getOrder(orderId),
  };
};

// Single order hook for direct use in components
export const useOrder = (orderId: string | number) => {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: () => ordersApi.getOrder(orderId),
    enabled: !!orderId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}; 
